import { useMemo } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import Select from "../../../Form/Select";
import DateField from "../../../Form/DateField";
import TimeField from "../../../Form/TimeField";
import TimezoneSelectField from "../../../Gallery/ShareModalMultiple/components/TimezoneSelectField";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  AUTOMATIONS_SCHEDULE_END,
  AUTOMATIONS_SCHEDULE_END_ALWAYS_ON,
  AUTOMATIONS_SCHEDULE_END_ALWAYS_ON_HELP,
  AUTOMATIONS_SCHEDULE_END_SCHEDULED,
  AUTOMATIONS_SCHEDULE_FREQUENCY,
  AUTOMATIONS_SCHEDULE_PUBLISHING_WINDOW,
  AUTOMATIONS_SCHEDULE_PUBLISHING_WINDOW_HELP,
  AUTOMATIONS_SCHEDULE_START,
  AUTOMATIONS_SCHEDULE_START_NOW,
  AUTOMATIONS_SCHEDULE_START_NOW_HELP,
  AUTOMATIONS_SCHEDULE_START_SCHEDULED,
  AUTOMATIONS_SCHEDULE_TIMEZONE,
  AUTOMATIONS_SCHEDULE_VALIDITY,
  AUTOMATIONS_STEP_SCHEDULE_HELP,
  AUTOMATIONS_STEP_SCHEDULE_TITLE,
} from "../../../../i18n/keysTranslations";
import { getTimezoneOptions } from "../../../../utils/timezone";
import {
  END_MODE_ALWAYS_ON,
  END_MODE_SCHEDULED,
  START_MODE_NOW,
  START_MODE_SCHEDULED,
} from "../../../../utils/publishingAutomationValidity";
import AutomationStepSection from "../AutomationStepSection";
import { AUTOMATION_FREQUENCY_OPTIONS } from "../../../../utils/publishingAutomationFrequency";
import { subtleBorderSx } from "../automationModalStyles";

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const StepSchedule = ({
  control,
  errors,
  businessName,
  formState,
  onChange,
  allowedFrequencies = null,
  onLockedCapability = () => {},
}) => {
  const { t } = useTranslationApp();
  const startLocked = Boolean(formState.startLocked);

  const frequencyOptions = useMemo(
    () =>
      AUTOMATION_FREQUENCY_OPTIONS.map((option) => ({
        label: option.labelKey,
        value: option.value,
      })),
    []
  );

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const minStartDate = startOfToday();

  return (
    <AutomationStepSection
      title={t(AUTOMATIONS_STEP_SCHEDULE_TITLE)}
      description={t(AUTOMATIONS_STEP_SCHEDULE_HELP, {
        NAME_BUSINESS: businessName,
      })}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {t(AUTOMATIONS_SCHEDULE_VALIDITY)}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <FieldBlock label={t(AUTOMATIONS_SCHEDULE_START)} labelWeight={400}>
              <RadioGroup
                value={formState.startMode}
                onChange={(event) => {
                  if (startLocked) return;
                  onChange({ startMode: event.target.value });
                }}
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <ValidityChoice
                  value={START_MODE_NOW}
                  title={t(AUTOMATIONS_SCHEDULE_START_NOW)}
                  description={t(AUTOMATIONS_SCHEDULE_START_NOW_HELP)}
                  disabled={startLocked}
                />
                <ValidityChoice
                  value={START_MODE_SCHEDULED}
                  title={t(AUTOMATIONS_SCHEDULE_START_SCHEDULED)}
                  disabled={startLocked}
                >
                  <ValidityDateRow
                    control={control}
                    errors={errors}
                    dateName="validityStartDate"
                    minDate={minStartDate}
                    disabled={
                      startLocked ||
                      formState.startMode !== START_MODE_SCHEDULED
                    }
                  />
                </ValidityChoice>
              </RadioGroup>
            </FieldBlock>

            <FieldBlock label={t(AUTOMATIONS_SCHEDULE_END)} labelWeight={400}>
              <RadioGroup
                value={formState.endMode}
                onChange={(event) => onChange({ endMode: event.target.value })}
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <ValidityChoice
                  value={END_MODE_ALWAYS_ON}
                  title={t(AUTOMATIONS_SCHEDULE_END_ALWAYS_ON)}
                  description={t(AUTOMATIONS_SCHEDULE_END_ALWAYS_ON_HELP)}
                />
                <ValidityChoice
                  value={END_MODE_SCHEDULED}
                  title={t(AUTOMATIONS_SCHEDULE_END_SCHEDULED)}
                >
                  <ValidityDateRow
                    control={control}
                    errors={errors}
                    dateName="validityEndDate"
                    minDate={minStartDate}
                    disabled={formState.endMode !== END_MODE_SCHEDULED}
                  />
                </ValidityChoice>
              </RadioGroup>
            </FieldBlock>
          </Box>
        </Box>

        <FieldBlock label={t(AUTOMATIONS_SCHEDULE_FREQUENCY)}>
          <Select
            name="frequencyKey"
            control={control}
            errors={errors}
            options={frequencyOptions}
            doTranslate
            variant="filled"
            fullWidth
            rules={{}}
            validationChange={async (value) => {
              if (
                !Array.isArray(allowedFrequencies) ||
                allowedFrequencies.includes(value)
              ) {
                return true;
              }
              onLockedCapability();
              return false;
            }}
          />
        </FieldBlock>

        <FieldBlock label={t(AUTOMATIONS_SCHEDULE_PUBLISHING_WINDOW)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1,
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <TimeField name="startTime" control={control} errors={errors} />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  flexShrink: 0,
                  display: { xs: "none", sm: "block" },
                }}
              >
                –
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <TimeField name="endTime" control={control} errors={errors} />
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35, display: "block" }}
            >
              {t(AUTOMATIONS_SCHEDULE_PUBLISHING_WINDOW_HELP, {
                NAME_BUSINESS: businessName,
              })}
            </Typography>
          </Box>
        </FieldBlock>

        <FieldBlock label={t(AUTOMATIONS_SCHEDULE_TIMEZONE)}>
          <TimezoneSelectField
            control={control}
            errors={errors}
            name="timezone"
            options={timezoneOptions}
          />
        </FieldBlock>
      </Box>
    </AutomationStepSection>
  );
};

const ValidityDateRow = ({ control, errors, dateName, minDate, disabled }) => (
  <Box
    onClick={(event) => event.stopPropagation()}
    onMouseDown={(event) => event.stopPropagation()}
    sx={{
      ...subtleBorderSx,
      overflow: "hidden",
      backgroundColor: "fields.background",
    }}
  >
    <DateField
      name={dateName}
      control={control}
      errors={errors}
      minDate={minDate}
      disabled={disabled}
      rules={{}}
      variant="filled"
      sx={{
        "& fieldset": { border: "none" },
        "& .MuiFilledInput-root": {
          backgroundColor: "transparent",
          "&:hover": { backgroundColor: "transparent" },
          "&.Mui-focused": { backgroundColor: "transparent" },
          "&:before, &:after": { display: "none" },
        },
        "& .MuiInputBase-root": {
          backgroundColor: "transparent",
        },
      }}
    />
  </Box>
);

const ValidityChoice = ({ value, title, description, disabled, children }) => (
  <Box
    sx={{
      ...subtleBorderSx,
      p: 2,
      opacity: disabled ? 0.7 : 1,
    }}
  >
    <FormControlLabel
      value={value}
      disabled={disabled}
      control={<Radio size="small" />}
      label={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.3,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.35, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                lineHeight: 1.35,
                display: "block",
                overflowWrap: "anywhere",
              }}
            >
              {description}
            </Typography>
          )}
          {children && (
            <Box sx={{ mt: description ? 0.7 : 1.2 }}>{children}</Box>
          )}
        </Box>
      }
      sx={{
        alignItems: "flex-start",
        m: 0,
        width: "100%",
        "& .MuiRadio-root": {
          p: 0,
          mt: "2px",
          mr: 1,
        },
        "& .MuiFormControlLabel-label": {
          width: "100%",
          minWidth: 0,
        },
      }}
    />
  </Box>
);

const FieldBlock = ({ label, children, labelWeight = 600 }) => (
  <FormControl fullWidth>
    <FormLabel sx={{ mb: 1, color: "background.paperContrastText" }}>
      <Typography variant="body2" fontWeight={labelWeight}>
        {label}
      </Typography>
    </FormLabel>
    {children}
  </FormControl>
);

export default StepSchedule;
